import React, { Fragment, useCallback } from 'react';
import {
  FlatList,
  Linking,
  ListRenderItemInfo,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {
  mbApplyTransparency,
  mbTextStyles,
} from '@mightybyte/rnw.utils.style-utils';
import { FONT_STYLES, textStyles } from '../../../constants/textStyles';
import GameControlIcon from '../../../resources/svgComponents/GameControlIcon';
import { trpc } from '../../../apiCalls/trpcClient';
import { Spacer } from '../../helperComponents/Spacer';
import { UIActivityIndicator } from '@mightybyte/rnw.components.activity-indicators';
import Card from '../Tabs/Results/components/Card';
import { mbGetSmallestImage } from '../../../utils/imageUtils';
import { ImageUrls } from '@sapphicsavvy/business';
import FastImage from 'react-native-fast-image';
import OverviewScreen from '../../helperComponents/OverviewScreen';

type MiniGameData = {
  id: string;
  title: string;
  link: string;
  imageUrl?: ImageUrls;
  setNumber: number;
  startDate: number;
  endDate: number;
  isExpired: boolean;
};

function MiniGameIcon({ imageUrl }: { imageUrl?: ImageUrls }) {
  const imageSource = mbGetSmallestImage(imageUrl, { asUri: true });

  if (imageSource) {
    return (
      <FastImage
        source={imageSource}
        style={styles.gameImage}
        resizeMode={FastImage.resizeMode.contain}
      />
    );
  }

  return <GameControlIcon size={55} color={'#FFFFFF'}/>;
}

export default function MiniGames() {
  const { data: miniGames, isLoading } = trpc.miniGames.getForCurrentSet.useQuery();

  const handleGamePress = useCallback((link: string) => {
    Linking.openURL(link);
  }, []);

  const renderItem = useCallback(({ item }: ListRenderItemInfo<MiniGameData>) => {
    return (
      <Card
        icon={<MiniGameIcon imageUrl={item.imageUrl}/>}
        color={['#FCAF41', '#FCAF41']}
        title={item.title}
        text={item.isExpired ? 'Wait for a new Set to play new games' : 'Play now'}
        enabled={!item.isExpired}
        isLocked={false}
        disabledMessage="Expired"
        onPress={() => handleGamePress(item.link)}
      />
    );
  }, [handleGamePress]);

  const EmptyComponent = useCallback(() => {
    if (isLoading) {
      return (
        <View style={styles.loading}>
          <UIActivityIndicator size={40} color="#EE3823" />
        </View>
      );
    }
    return (
      <View style={styles.emptyState}>
        <GameControlIcon size={80} />
        <Spacer height={20} />
        <Text style={styles.emptyStateTitle}>No Mini-Games Available</Text>
        <Text style={styles.emptyStateText}>
          Check back later for new mini-games!
        </Text>
      </View>
    );
  }, [isLoading]);

  const keyExtractor = useCallback((item: MiniGameData) => item.id, []);
  const itemSeparator = useCallback(() => <Spacer height={15} />, []);

  return (
    <OverviewScreen
      showBackBtn
      gradientColors={['#EF3A24', '#F9943B']}
      topSectionStyle={styles.topSection}
      topSectionChildren={
        <Fragment>
          <GameControlIcon size={80} />
          <Spacer height={10} />
          <Text style={styles.titleText}>MINI-GAMES</Text>
        </Fragment>
      }
      bottomSectionStyle={styles.bottomSection}
    >
      <FlatList
        data={miniGames ?? []}
        renderItem={renderItem}
        ItemSeparatorComponent={itemSeparator}
        keyExtractor={keyExtractor}
        contentContainerStyle={styles.flatListContainer}
        style={styles.flatList}
        ListEmptyComponent={EmptyComponent}
        showsVerticalScrollIndicator={false}
      />
    </OverviewScreen>
  );
}

const styles = StyleSheet.create({
  topSection: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    height: '30%',
  },
  bottomSection: {
    paddingTop: 0,
    height: '80%',
  },
  titleText: mbTextStyles([
    textStyles.normalText,
    {
      fontFamily: FONT_STYLES.Tabasco_400,
      fontSize: 30,
      fontWeight: '400',
      color: '#FFFFFF',
      textAlign: 'center',
    },
  ]),
  flatList: {
    borderTopLeftRadius: 40,
    borderTopRightRadius: 40,
  },
  flatListContainer: {
    paddingTop: 30,
    paddingHorizontal: 20,
    paddingBottom: 40,
  },
  loading: {
    paddingTop: 30,
    alignItems: 'center',
  },
  emptyState: {
    paddingTop: 30,
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyStateTitle: mbTextStyles([
    textStyles.normalText,
    {
      fontFamily: FONT_STYLES.Tabasco_400,
      fontSize: 24,
      fontWeight: '400',
      color: '#292E30',
      textAlign: 'center',
      marginBottom: 10,
    },
  ]),
  emptyStateText: mbTextStyles([
    textStyles.normalText,
    {
      fontSize: 16,
      fontWeight: '400',
      color: mbApplyTransparency('#677278', 0.8),
      textAlign: 'center',
    },
  ]),
  gameImage: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
});
